/* The two ways these charts reach a component's field values, adapted onto one reader.
 *
 * Viewpoint mode resolves everything against the traversal's `EnhancedScopeData`, and workspace
 * mode against the Backbone-backed interfaces. The bubble chart carries a near-identical
 * extraction path per mode; adapting both onto `XyzComponentReader` keeps the extraction itself
 * in one place, so a fix to how a field value is read cannot land in one mode only. */

import type { ArdoqId } from '@ardoq/api-types';
import type {
  EnhancedScopeData,
  GetCssClassNamesOption,
} from '@ardoq/data-model';
import {
  componentUtils,
  enhancedScopeDataOperations,
  fieldUtils,
  type FilterColorData,
} from '@ardoq/scope-data';
import type { XyzComponentReader } from '@ardoq/xyz-charts';

/** Colours are read as marks rather than as CSS backgrounds, matching the bubble chart. */
const COLOR_OPTIONS: GetCssClassNamesOption = { useAsBackgroundStyle: false };

export type ScopeDataReaderArgs = {
  enhancedScopeData: EnhancedScopeData;
  filterColorData: FilterColorData;
  /**
   * Labels resolved upstream rather than here.
   *
   * Building them needs the locale, the reference label filters and the labels interface, none
   * of which this reader should know about - and the existing helper already handles the
   * multi-label case these charts do not support.
   */
  labels: Map<ArdoqId, string>;
};

export const createScopeDataReader = ({
  enhancedScopeData,
  filterColorData,
  labels,
}: ScopeDataReaderArgs): XyzComponentReader => ({
  getFieldValue: (componentId, fieldName) =>
    fieldUtils.getFieldValueForComponentOrReference(
      enhancedScopeData,
      componentId,
      fieldName
    ),
  getLabel: componentId =>
    labels.get(componentId) ??
    enhancedScopeDataOperations.getComponent(enhancedScopeData, componentId)
      ?.name ??
    '',
  getColors: componentId =>
    componentUtils.getComponentDisplayColorAsSVGAttributes(
      enhancedScopeData,
      filterColorData,
      componentId,
      COLOR_OPTIONS
    ),
});

/**
 * What workspace mode needs off the app's interfaces.
 *
 * Declared here as its own type rather than reusing `BubbleChartViewInterface`: that carries
 * around thirty members, and depending on all of them to read three would tie these charts to
 * every future change to the bubble chart's interface.
 */
export type XyzViewInterface = {
  getComponentFieldValue: (componentId: ArdoqId, fieldName: string) => unknown;
  getComponentDisplayLabel: (componentId: ArdoqId) => string | null;
  getComponentDisplayColor: (
    componentId: ArdoqId,
    options: GetCssClassNamesOption
  ) => { fill?: string; stroke?: string };
};

export const createViewInterfaceReader = (
  viewInterface: XyzViewInterface
): XyzComponentReader => ({
  getFieldValue: (componentId, fieldName) =>
    viewInterface.getComponentFieldValue(componentId, fieldName),
  getLabel: componentId =>
    viewInterface.getComponentDisplayLabel(componentId) ?? '',
  getColors: componentId =>
    viewInterface.getComponentDisplayColor(componentId, COLOR_OPTIONS),
});
